// src/utils/hudState.ts

import { LS_KEYS } from "@/utils/constants";
import { AudioController } from "@/utils/audioController";
import { nudgeMuteButton } from "@/utils/muteNudge";
import { log } from "./debug";
import { setGlobalCaptionsEnabled } from "../elements/captionsElement";

/**
 * HUD State Manager
 * -----------------
 * Central source of truth for HUD state across reels/narration sections:
 *  - Captions (on/off)
 *  - Mute (video mute and narration mute)
 *  - Like button toggle
 */

export const CC_PREF_KEY = "sc:cc-pref"; // "on" | "off"

// --------- Captions handle discovery (no hard import to avoid TS export mismatch) ----------
type CaptionMode = "on" | "off" | "auto";
type CaptionHandleLike = {
  mode: CaptionMode;
  setMode: (m: CaptionMode, opts?: { source?: "user" | "system" }) => void;
  show?: () => void;
  hide?: () => void;
};

/**
 * Best-effort way to find the captions handle for a section.
 * We check a few common attachment points to avoid a hard import.
 */
function getCaptionsHandle(section: HTMLElement): CaptionHandleLike | null {
  // Common places projects stash this:
  const bySection =
    (section as any).__scCaptionHandle ||
    (section as any).__captionsHandle ||
    null;
  if (bySection) return bySection as CaptionHandleLike;

  const layer =
    section.querySelector<HTMLElement>(".sc-captions-layer, .srt-caption") ||
    null;
  const handle = (layer as any)?.__handle || null;
  return (handle as CaptionHandleLike) ?? null;
}

/* ------------------ Captions ------------------ */
export function readCcPref(): boolean {
  try {
    // Primary key (colon)
    const v = localStorage.getItem(CC_PREF_KEY);
    if (v === "on") return true;
    if (v === "off") return false;

    // Backward-compat: read old dash key if present
    const legacy = localStorage.getItem("sc-cc-pref");
    if (legacy === "on") return true;
    if (legacy === "off") return false;

    // default ON
    return true;
  } catch {
    return true;
  }
}
export function writeCcPref(on: boolean) {
  try {
    localStorage.setItem(CC_PREF_KEY, on ? "on" : "off");
    // Also write legacy key for safety (can remove later)
    localStorage.setItem("sc-cc-pref", on ? "on" : "off");
  } catch {}
}

export function getCcButtons(section: HTMLElement): HTMLElement[] {
  return Array.from(
    section.querySelectorAll<HTMLElement>(
      ".video-reel-cc-button, .cc-button"
    )
  );
}

/** Update only the CC buttons' UI (no DOM attributes, no storage). */
function updateCcButtons(section: HTMLElement, isOn: boolean): void {
  for (const btn of getCcButtons(section)) {
    btn.classList.toggle("active", isOn);
    btn.setAttribute("aria-pressed", String(isOn));
    btn.setAttribute(
      "aria-label",
      `Closed captions (${isOn ? "on" : "off"})`
    );
  }
}

/** Delegate CC state to the captions handle if present; otherwise set attribute & update buttons. */
export function applyCcState(section: HTMLElement, isOn: boolean): void {
  const handle = getCaptionsHandle(section);
  if (handle) {
    handle.setMode(isOn ? "on" : "off");
  } else {
    // Fallback so CSS/renderer can hide if it honors the attribute
    section.setAttribute("data-cc-hidden", isOn ? "0" : "1");
    document.dispatchEvent(
      new CustomEvent("sc:cc-change", {
        detail: { active: isOn, section }
      })
    );
  }
  updateCcButtons(section, isOn);
}

/* ------------------ Captions (centralized helpers) ------------------ */
/** Ensure a default CC preference exists (default: "on"). Call once at boot. */
export function ensureCcPrefInitialized() {
  if (
    localStorage.getItem(CC_PREF_KEY) == null &&
    localStorage.getItem("sc-cc-pref") == null
  ) {
    writeCcPref(true);
  }
}

/** Single place to persist and reflect CC state (DOM, buttons, and event). */
export function setCc(section: HTMLElement, isOn: boolean): void {
  writeCcPref(isOn); // persist
  setGlobalCaptionsEnabled(isOn);
  const handle = getCaptionsHandle(section);
  if (handle) {
    handle.setMode(isOn ? "on" : "off", { source: "user" }); // updates DOM + fires event
  } else {
    // Fallback path if no handle discovered
    section.setAttribute("data-cc-hidden", isOn ? "0" : "1");
    document.dispatchEvent(
      new CustomEvent("sc:cc-change", {
        detail: { active: isOn, section }
      })
    );
  }
  updateCcButtons(section, isOn); // keep button UI in sync
}

/** Convenience toggle using the section's current `data-cc-hidden` flag. */
export function toggleCc(section: HTMLElement): void {
  const handle = getCaptionsHandle(section);
  let currentlyOn: boolean;
  if (handle) {
    currentlyOn = handle.mode !== "off";
  } else {
    const attr = section.getAttribute("data-cc-hidden");
    // Default ON if unset
    currentlyOn = attr !== "1";
  }
  log("[HUD] toggleCc: handle.mode=", (getCaptionsHandle(section) as any)?.mode, "computedCurrentlyOn=", currentlyOn);
  setCc(section, !currentlyOn);
}

/* ------------------ Mute (shared UI) ------------------ */
export function getMuteButton(section: HTMLElement): HTMLElement | null {
  return section.querySelector<HTMLElement>(
    ".video-mute-button, .mute-button, .video-reel-mute-button, .hud-btn.mute-button, .narration-mute-button"
  );
}

export function updateMuteButtonUI(
  section: HTMLElement,
  muted: boolean
): void {
  const btn = getMuteButton(section);
  if (!btn) return;

  // If narration button exposes an internal updater, use it
  const updater = (btn as any).__update as
    | ((muted: boolean) => void)
    | undefined;
  if (updater) {
    updater(muted);
  } else {
    const isNarration = btn.classList.contains("narration-mute-button");
    const isVideo = !isNarration; // default to video unless explicitly narration

    // ARIA + title
    if (isNarration) {
      btn.setAttribute(
        "aria-label",
        muted ? "Unmute narration" : "Mute narration"
      );
      btn.setAttribute(
        "title",
        muted ? "Unmute narration" : "Mute narration"
      );
    } else if (isVideo) {
      btn.setAttribute("aria-label", muted ? "Unmute video" : "Mute video");
      btn.setAttribute("title", muted ? "Unmute video" : "Mute video");
    }

    btn.setAttribute("aria-pressed", String(!muted)); // pressed = sound ON
    btn.classList.toggle("active", !muted); // active = sound ON
    btn.classList.toggle("muted", muted);
  }

  // Persist generic audio UI state for return visits
  try {
    localStorage.setItem(
      (LS_KEYS as any)?.audioMuted ?? "storycore-audio-muted",
      String(muted)
    );
  } catch {}
}

/** Mirror the actual video element's mute state into the HUD button. */
export function reflectVideoMuteState(
  section: HTMLElement,
  video?: HTMLVideoElement | null
): void {
  const v = video || section.querySelector<HTMLVideoElement>("video");
  if (!v) return;
  updateMuteButtonUI(section, v.muted);
}

/**
 * Toggle the video mute state from a user gesture.
 * If turning sound ON, we route through AudioController so it can
 * correctly unlock audio under this gesture and respect global state.
 * Returns the final muted state (true = muted).
 */
export async function userToggleVideoMute(
  section: HTMLElement,
  video?: HTMLVideoElement | null
): Promise<boolean> {
  const v = video || section.querySelector<HTMLVideoElement>("video");
  if (!v) return true;

  const currentlyMuted = v.muted || v.volume === 0;

  try {
    if (currentlyMuted) {
      // User is trying to turn sound ON
      const ok = await AudioController.requestUnmuteFromUserGesture(v);
      if (!ok) {
        // If browser rejects audio, keep it muted to avoid lying UI
        AudioController.requestMute(v);
      }
    } else {
      // User is turning sound OFF
      AudioController.requestMute(v);
    }
  } catch {
    // On any error, fall back to muted for safety
    try { AudioController.requestMute(v); } catch {}
  }

  const finalMuted = v.muted || v.volume === 0;
  updateMuteButtonUI(section, finalMuted);
  return finalMuted;
}

/**
 * Call this from autoplay catch handlers when a sound-on start fails.
 * It force-syncs UI to muted so the button doesn't lie to the user.
 */
export function onAutoplayWithAudioFailed(
  section: HTMLElement,
  video?: HTMLVideoElement | null
): void {
  const v = video || section.querySelector<HTMLVideoElement>("video");
  if (v) v.muted = true;
  updateMuteButtonUI(section, true);
  // One-time visual nudge so users know how to enable sound again
  try { nudgeMuteButton(section); } catch {}
}

/* ------------------ Narration-specific helpers ------------------ */
/**
 * Sync narration HUD button with the global audio mute state.
 */
export function syncNarrationHud(section: HTMLElement): void {
  const muted = AudioController.isMuted();
  updateMuteButtonUI(section, muted);
}

/**
 * Toggle global narration mute state and update HUD button.
 */
export function toggleNarrationMute(section: HTMLElement, forced?: boolean): void {
  const next =
    typeof forced === "boolean" ? forced : !AudioController.isMuted();
  AudioController.setMuted(next, "hud");
  try { (window as any).__storycore_narration_muted = next; } catch {}
  updateMuteButtonUI(section, next);
}

/* ------------------ Like ------------------ */
export function getLikeButton(section: HTMLElement): HTMLElement | null {
  return section.querySelector<HTMLElement>(".like-button");
}
export function toggleLike(section: HTMLElement): void {
  const btn = getLikeButton(section);
  if (!btn) return;
  const active = btn.classList.toggle("active");
  btn.setAttribute("aria-pressed", String(active));
}

/* ------------------ Sync (for Reels) ------------------ */
export function syncHudForSection(section: HTMLElement): void {
  // Captions
  const handle = getCaptionsHandle(section);
  if (handle) {
    // Set the handle to the persisted preference; it will update layer + emit event.
    const prefOn = readCcPref();
    handle.setMode(prefOn ? "on" : "off");
    updateCcButtons(section, prefOn);
  } else {
    // No handle yet (e.g., non-reel section). Just reflect button UI from preference.
    updateCcButtons(section, readCcPref());
  }

  // Mute → prefer actual video element state (if present)
  const v = section.querySelector<HTMLVideoElement>("video");
  if (v) {
    reflectVideoMuteState(section, v);
  } else {
    // Narration-only sections fall back to global audio state
    if (section.querySelector(".narration-mute-button")) {
      syncNarrationHud(section);
    }
  }
}

/* ------------------ Refresh (Unified) ------------------ */
/**
 * Refresh HUD state (mute, captions, like) for any section.
 * - For narration HUD, syncs narration mute button with global state
 * - For reel HUD, syncs captions + video mute buttons
 * - Always ensures like button aria matches visual state
 */
export function refreshHudState(section: HTMLElement): void {
  try {
    syncHudForSection(section);

    // Like button
    const likeBtn = getLikeButton(section);
    if (likeBtn) {
      const active = likeBtn.classList.contains("active");
      likeBtn.setAttribute("aria-pressed", String(active));
    }
  } catch (e) {
    console.warn("[HUDState] Failed to refresh HUD state", e);
  }
}
